
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Define a more complete PatientData type
export interface PatientData {
  last_name: string;
  first_name: string;
  birth_name?: string;
  birth_date?: string | Date;
  file_number?: string;
  archive_number?: string;
  address?: string;
  phone?: string;
  email?: string;
  insurance_number?: string;
  complementary_number?: string;
  medical_info?: Record<string, string>;
  medications?: Array<any>;
  journal_entries?: Array<any>;
  gender?: string;
  marital_status?: string;
  children_count?: number;
  height?: number | string;
  weight?: number | string;
  blood_type?: string;
  treating_doctor?: string;
  smoker?: boolean;
  cigarettes_per_day?: number;
}

// Define a type to handle the lastAutoTable property
interface AutoTableResult {
  finalY: number;
}

// Generate patient file PDF based on selected model type
export const generatePatientFilePDF = async (patientData: PatientData, modelType: 'standard' | 'detailed' | 'compact' | 'professional' = 'professional') => {
  // Create a new PDF document
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  console.log("Generating PDF with patient data:", patientData);
  
  // Set up different model formats
  switch (modelType) {
    case 'detailed':
      return generateDetailedPatientFilePDF(doc, patientData, pageWidth);
    case 'compact':
      return generateCompactPatientFilePDF(doc, patientData, pageWidth);
    case 'professional':
      return generateProfessionalPatientFilePDF(doc, patientData, pageWidth);
    case 'standard':
    default:
      return generateStandardPatientFilePDF(doc, patientData, pageWidth);
  }
};

// Function to safely format date
const formatDate = (dateValue: any) => {
  if (!dateValue) return "N/A";
  try {
    const date = new Date(dateValue);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Date invalide";
  }
};

// Format file creation timestamp
const formatTimestamp = (date = new Date()) => {
  return date.toLocaleDateString('fr-FR') + ' à ' + 
    date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

// Standard patient file PDF layout
const generateStandardPatientFilePDF = (doc: jsPDF, patientData: PatientData, pageWidth: number) => {
  // Add title with file number
  doc.setFontSize(14);
  doc.setTextColor(44, 62, 80);
  const fileNumber = patientData.file_number ? `Dossier ${patientData.file_number}` : "DOSSIER PATIENT";
  doc.text(fileNumber, 14, 15);
  
  // Add patient full name in bigger font
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  const fullName = `${patientData.last_name} ${patientData.first_name}`.toUpperCase();
  doc.text(fullName, 14, 25);
  doc.setFont("helvetica", "normal");
  
  // Patient general information section
  doc.setFillColor(230, 230, 230);
  doc.rect(10, 30, pageWidth - 20, 7, 'F');
  doc.setFontSize(12);
  doc.setTextColor(30, 30, 30);
  doc.text("Informations générales", 14, 35);
  
  // General information table
  autoTable(doc, {
    startY: 40,
    theme: 'plain',
    headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0] },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 40 },
      1: { cellWidth: 'auto' }
    },
    body: [
      ["Nom", patientData.last_name || "N/A"],
      ["Prénom", patientData.first_name || "N/A"],
      ["Date de naissance", formatDate(patientData.birth_date)],
      ["Sexe", patientData.gender || "-"],
      ["Situation familiale", patientData.marital_status || "-"],
      ["Enfants", patientData.children_count ? patientData.children_count.toString() : "-"],
      ["N° Sécurité sociale", patientData.insurance_number || "-"]
    ]
  });
  
  // Get the Y position after the general info table
  const generalInfoTable = (doc as any).lastAutoTable as AutoTableResult;
  
  // Medical information section
  doc.setFillColor(230, 230, 230);
  doc.rect(10, generalInfoTable.finalY + 5, pageWidth - 20, 7, 'F');
  doc.setFontSize(12);
  doc.text("Informations médicales", 14, generalInfoTable.finalY + 10);
  
  // Medical information table
  autoTable(doc, {
    startY: generalInfoTable.finalY + 15,
    theme: 'plain',
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 40 },
      1: { cellWidth: 'auto' }
    },
    body: [
      ["Taille", patientData.height ? `${patientData.height} cm` : "-"],
      ["Poids", patientData.weight ? `${patientData.weight} kg` : "-"],
      ["Groupe sanguin", patientData.blood_type || "-"],
      ["Médecin traitant", patientData.treating_doctor || "-"],
      ["Mutuelle", patientData.complementary_number || "-"],
      ["Fumeur", patientData.smoker ? "Oui" : "Non"],
      patientData.smoker ? ["Cigarettes/jour", patientData.cigarettes_per_day?.toString() || "N/A"] : ["", ""]
    ].filter(row => row[0] !== "") // Remove empty rows
  });
  
  // Get the Y position after the medical info table
  const medicalInfoTable = (doc as any).lastAutoTable as AutoTableResult;
  
  // Contact information section
  doc.setFillColor(230, 230, 230);
  doc.rect(10, medicalInfoTable.finalY + 5, pageWidth - 20, 7, 'F');
  doc.setFontSize(12);
  doc.text("Coordonnées", 14, medicalInfoTable.finalY + 10);
  
  // Contact information table
  autoTable(doc, {
    startY: medicalInfoTable.finalY + 15,
    theme: 'plain',
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 40 },
      1: { cellWidth: 'auto' }
    },
    body: [
      ["Email", patientData.email || "N/A"],
      ["Téléphone", patientData.phone || "N/A"],
      ["Adresse", patientData.address || "-"]
    ]
  });
  
  // Timestamps at the bottom
  const now = new Date();
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(`Dossier créé le ${formatTimestamp(now)}`, 14, doc.internal.pageSize.getHeight() - 20);
  doc.text(`Dernière modification le ${formatTimestamp(now)}`, 14, doc.internal.pageSize.getHeight() - 15);
  
  // Save the PDF with patient name
  const filename = `Dossier_${patientData.last_name}_${patientData.first_name}.pdf`;
  doc.save(filename);
  
  return doc;
};

// Detailed patient file PDF layout
const generateDetailedPatientFilePDF = (doc: jsPDF, patientData: PatientData, pageWidth: number) => {
  // Add header with file number and clinic details
  doc.setFillColor(41, 128, 185);
  doc.rect(0, 0, pageWidth, 30, "F");
  doc.setFontSize(22);
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  const fileNumber = patientData.file_number ? `DOSSIER ${patientData.file_number}` : "DOSSIER MÉDICAL";
  doc.text(fileNumber, pageWidth / 2, 18, { align: "center" });
  doc.setFontSize(16);
  const fullName = `${patientData.last_name} ${patientData.first_name}`.toUpperCase();
  doc.text(fullName, pageWidth / 2, 26, { align: "center" });
  doc.setFont("helvetica", "normal");
  
  // Patient photo placeholder (if needed)
  doc.setDrawColor(200, 200, 200);
  doc.setFillColor(240, 240, 240);
  doc.roundedRect(14, 40, 40, 50, 3, 3, 'FD');
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text("Photo", 34, 65, { align: "center" });
  
  // Patient general information section
  doc.setFontSize(14);
  doc.setTextColor(41, 128, 185);
  doc.setFont("helvetica", "bold");
  doc.text("INFORMATIONS GÉNÉRALES", 65, 50);
  doc.setFont("helvetica", "normal");
  
  // General information table
  autoTable(doc, {
    startY: 55,
    margin: { left: 65 },
    theme: 'plain',
    tableWidth: pageWidth - 80,
    styles: { fontSize: 10 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 50 },
      1: { cellWidth: 'auto' }
    },
    body: [
      ["Nom", patientData.last_name || "N/A"],
      ["Prénom", patientData.first_name || "N/A"],
      ["Date de naissance", formatDate(patientData.birth_date)],
      ["Sexe", patientData.gender || "-"],
      ["Situation familiale", patientData.marital_status || "-"],
      ["Enfants", patientData.children_count ? patientData.children_count.toString() : "-"],
      ["N° Sécurité sociale", patientData.insurance_number || "-"],
      ["N° Dossier", patientData.file_number || "-"]
    ]
  });
  
  // Medical information section
  doc.setFontSize(14);
  doc.setTextColor(41, 128, 185);
  doc.setFont("helvetica", "bold");
  doc.text("INFORMATIONS MÉDICALES", 14, 110);
  doc.setFont("helvetica", "normal");
  
  // Add a horizontal line
  doc.setDrawColor(41, 128, 185);
  doc.setLineWidth(0.5);
  doc.line(14, 113, pageWidth - 14, 113);
  
  // Medical information in two columns
  autoTable(doc, {
    startY: 118,
    theme: 'plain',
    tableWidth: (pageWidth - 30) / 2,
    margin: { left: 14 },
    styles: { fontSize: 10 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 45 },
      1: { cellWidth: 'auto' }
    },
    body: [
      ["Taille", patientData.height ? `${patientData.height} cm` : "-"],
      ["Poids", patientData.weight ? `${patientData.weight} kg` : "-"],
      ["Groupe sanguin", patientData.blood_type || "-"],
      ["Fumeur", patientData.smoker ? "Oui" : "Non"],
      patientData.smoker ? ["Cigarettes/jour", patientData.cigarettes_per_day?.toString() || "N/A"] : ["", ""]
    ].filter(row => row[0] !== "") // Remove empty rows
  });
  
  // Second column for medical info
  autoTable(doc, {
    startY: 118,
    theme: 'plain',
    tableWidth: (pageWidth - 30) / 2,
    margin: { left: pageWidth / 2 },
    styles: { fontSize: 10 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 45 },
      1: { cellWidth: 'auto' }
    },
    body: [
      ["Médecin traitant", patientData.treating_doctor || "-"],
      ["Mutuelle", patientData.complementary_number || "-"],
      ["Allergies", (patientData.medical_info?.allergies) || "-"]
    ]
  });
  
  // Contact information section
  doc.setFontSize(14);
  doc.setTextColor(41, 128, 185);
  doc.setFont("helvetica", "bold");
  doc.text("COORDONNÉES", 14, 160);
  doc.setFont("helvetica", "normal");
  
  // Add a horizontal line
  doc.line(14, 163, pageWidth - 14, 163);
  
  // Contact information table
  autoTable(doc, {
    startY: 168,
    theme: 'plain',
    styles: { fontSize: 10 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 30 },
      1: { cellWidth: 'auto' }
    },
    body: [
      ["Email", patientData.email || "N/A"],
      ["Téléphone", patientData.phone || "N/A"],
      ["Adresse", patientData.address || "-"]
    ]
  });
  
  // Notes section
  doc.setFontSize(14);
  doc.setTextColor(41, 128, 185);
  doc.setFont("helvetica", "bold");
  doc.text("NOTES", 14, 200);
  doc.setFont("helvetica", "normal");
  
  // Add a horizontal line
  doc.line(14, 203, pageWidth - 14, 203);
  
  // Empty notes box
  doc.setDrawColor(200, 200, 200);
  doc.setFillColor(250, 250, 250);
  doc.rect(14, 208, pageWidth - 28, 40, 'FD');
  
  // Footer with timestamps
  const now = new Date();
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(`Dossier créé le ${formatTimestamp(now)}`, 14, doc.internal.pageSize.getHeight() - 15);
  doc.text(`Dernière modification le ${formatTimestamp(now)}`, pageWidth - 14, doc.internal.pageSize.getHeight() - 15, { align: "right" });
  
  // Save the PDF with patient name and file number
  const fileId = patientData.file_number ? `_${patientData.file_number}` : '';
  const filename = `Dossier_${patientData.last_name}_${patientData.first_name}${fileId}.pdf`;
  doc.save(filename);
  
  return doc;
};

// Compact patient file PDF layout
const generateCompactPatientFilePDF = (doc: jsPDF, patientData: PatientData, pageWidth: number) => {
  // Add title
  doc.setFontSize(14);
  doc.setTextColor(60, 60, 60);
  doc.setFont("helvetica", "bold");
  doc.text("FICHE PATIENT", pageWidth / 2, 15, { align: "center" });
  
  // Patient name and file number
  doc.setFontSize(12);
  const fileNumber = patientData.file_number ? `Dossier ${patientData.file_number}` : "";
  doc.text(fileNumber, pageWidth / 2, 22, { align: "center" });
  doc.setFontSize(16);
  const fullName = `${patientData.last_name} ${patientData.first_name}`.toUpperCase();
  doc.text(fullName, pageWidth / 2, 30, { align: "center" });
  doc.setFont("helvetica", "normal");
  
  // All information in a single compact table
  autoTable(doc, {
    startY: 35,
    theme: 'striped',
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: [80, 80, 80] },
    alternateRowStyles: { fillColor: [240, 240, 240] },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 35 },
      1: { cellWidth: 'auto' }
    },
    body: [
      ["Date de naissance", formatDate(patientData.birth_date)],
      ["Sexe", patientData.gender || "-"],
      ["Situation", patientData.marital_status || "-"],
      ["Téléphone", patientData.phone || "N/A"],
      ["Email", patientData.email || "N/A"],
      ["Adresse", patientData.address || "-"],
      ["Taille", patientData.height ? `${patientData.height} cm` : "-"],
      ["Poids", patientData.weight ? `${patientData.weight} kg` : "-"],
      ["Groupe sanguin", patientData.blood_type || "-"],
      ["Mutuelle", patientData.complementary_number || "-"],
      ["Fumeur", patientData.smoker ? "Oui" : "Non"]
    ]
  });
  
  // Footer
  const now = new Date();
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(`Généré le ${formatTimestamp(now)}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: "center" });
  
  // Save the PDF
  const filename = `Fiche_${patientData.last_name}_${patientData.first_name}.pdf`;
  doc.save(filename);
  
  return doc;
};

// Professional patient file PDF layout with modern design
const generateProfessionalPatientFilePDF = (doc: jsPDF, patientData: PatientData, pageWidth: number) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // Modern header with gradient effect
  doc.setFillColor(70, 130, 180); // Steel blue
  doc.rect(0, 0, pageWidth, 40, 'F');
  
  // Add subtle pattern to header
  for (let i = 0; i < pageWidth; i += 4) {
    doc.setDrawColor(65, 120, 170);
    doc.setLineWidth(0.1);
    doc.line(i, 0, i, 40);
  }
  
  // Logo placeholder in top left
  doc.setFillColor(255, 255, 255);
  doc.circle(20, 20, 10, 'F');
  doc.setTextColor(70, 130, 180);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("MED", 20, 22, { align: "center" });
  
  // Dossier Medical text
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.text("DOSSIER MÉDICAL", pageWidth/2, 20, { align: "center" });
  
  // File number with badge effect
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(pageWidth - 60, 10, 45, 15, 3, 3, 'F');
  doc.setTextColor(70, 130, 180);
  doc.setFontSize(10);
  doc.text("N° DOSSIER", pageWidth - 38, 17, { align: "center" });
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text(patientData.file_number || "---", pageWidth - 38, 23, { align: "center" });
  
  // Patient name section
  doc.setFillColor(245, 245, 245);
  doc.rect(0, 40, pageWidth, 20, 'F');
  doc.setTextColor(50, 50, 50);
  doc.setFontSize(16);
  const fullName = `${patientData.last_name} ${patientData.first_name}`.toUpperCase();
  doc.text(fullName, pageWidth / 2, 54, { align: "center" });
  
  // Date generated
  const now = new Date();
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text(`${formatDate(now)}`, pageWidth - 15, 54, { align: "right" });
  
  // Content area
  let yPos = 75;
  
  // Function to create sections
  const createSection = (title: string, yPosition: number, color: number[] = [70, 130, 180]) => {
    doc.setFillColor(color[0], color[1], color[2]);
    doc.roundedRect(12, yPosition, pageWidth - 24, 8, 1, 1, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(title, 18, yPosition + 5.5);
    return yPosition + 12;
  };
  
  // Information générale section
  yPos = createSection("INFORMATIONS GÉNÉRALES", yPos);
  
  // Personal info in a clean table
  autoTable(doc, {
    startY: yPos,
    theme: 'grid',
    styles: { fontSize: 10, cellPadding: 4 },
    headStyles: { fillColor: [240, 240, 240], textColor: [70, 70, 70], fontStyle: 'bold' },
    tableLineColor: [220, 220, 220],
    tableLineWidth: 0.1,
    margin: { left: 15, right: 15 },
    head: [['Information', 'Détail']],
    body: [
      ["Nom", patientData.last_name || "N/A"],
      ["Prénom", patientData.first_name || "N/A"],
      ["Nom de naissance", patientData.birth_name || "-"],
      ["Date de naissance", formatDate(patientData.birth_date)],
      ["Sexe", patientData.gender || "-"],
      ["Situation familiale", patientData.marital_status || "-"],
      ["Nombre d'enfants", patientData.children_count !== undefined ? patientData.children_count.toString() : "-"]
    ]
  });
  
  // Get the Y position after the table
  const generalInfoTable = (doc as any).lastAutoTable as AutoTableResult;
  yPos = generalInfoTable.finalY + 10;
  
  // Medical information section
  yPos = createSection("INFORMATIONS MÉDICALES", yPos, [76, 175, 80]);
  
  // Two column layout for medical information
  const colWidth = (pageWidth - 35) / 2;
  
  // Left column
  autoTable(doc, {
    startY: yPos,
    theme: 'grid',
    styles: { fontSize: 10, cellPadding: 4 },
    tableLineColor: [220, 220, 220],
    tableLineWidth: 0.1,
    margin: { left: 15 },
    tableWidth: colWidth,
    head: [['Information', 'Détail']],
    headStyles: { fillColor: [240, 240, 240], textColor: [70, 70, 70], fontStyle: 'bold' },
    body: [
      ["Taille", patientData.height ? `${patientData.height} cm` : "-"],
      ["Poids", patientData.weight ? `${patientData.weight} kg` : "-"],
      ["Groupe sanguin", patientData.blood_type || "-"],
      ["Allergies", patientData.medical_info?.allergies || "-"]
    ]
  });
  
  // Right column
  autoTable(doc, {
    startY: yPos,
    theme: 'grid',
    styles: { fontSize: 10, cellPadding: 4 },
    tableLineColor: [220, 220, 220],
    tableLineWidth: 0.1,
    margin: { left: pageWidth / 2 - 2 },
    tableWidth: colWidth,
    head: [['Information', 'Détail']],
    headStyles: { fillColor: [240, 240, 240], textColor: [70, 70, 70], fontStyle: 'bold' },
    body: [
      ["Médecin traitant", patientData.treating_doctor || "-"],
      ["N° Sécurité sociale", patientData.insurance_number || "-"],
      ["Mutuelle", patientData.complementary_number || "-"],
      ["Fumeur", patientData.smoker ? `Oui${patientData.cigarettes_per_day ? ` (${patientData.cigarettes_per_day}/jour)` : ''}` : "Non"]
    ]
  });
  
  // Get the Y position after the medical tables
  const medicalInfoTable = (doc as any).lastAutoTable as AutoTableResult;
  yPos = medicalInfoTable.finalY + 10;
  
  // Contact information section
  yPos = createSection("COORDONNÉES", yPos, [41, 128, 185]);
  
  // Contact information in a single table
  autoTable(doc, {
    startY: yPos,
    theme: 'grid',
    styles: { fontSize: 10, cellPadding: 4 },
    headStyles: { fillColor: [240, 240, 240], textColor: [70, 70, 70], fontStyle: 'bold' },
    tableLineColor: [220, 220, 220],
    tableLineWidth: 0.1,
    margin: { left: 15, right: 15 },
    head: [['Contact', 'Détail']],
    body: [
      ["Email", patientData.email || "N/A"],
      ["Téléphone", patientData.phone || "N/A"],
      ["Adresse", patientData.address || "-"]
    ]
  });
  
  // Get the Y position after the contact table
  const contactInfoTable = (doc as any).lastAutoTable as AutoTableResult;
  yPos = contactInfoTable.finalY + 10;
  
  // Add QR code placeholder (for digital version)
  if (yPos < pageHeight - 50) {
    doc.setFillColor(245, 245, 245);
    doc.roundedRect(pageWidth - 40, pageHeight - 40, 25, 25, 2, 2, 'F');
    doc.setFontSize(6);
    doc.setTextColor(100, 100, 100);
    doc.text("QR CODE", pageWidth - 27.5, pageHeight - 25, { align: "center" });
  }
  
  // Add footer with logo and text
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.5);
  doc.line(15, pageHeight - 15, pageWidth - 15, pageHeight - 15);
  
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.setFont("helvetica", "normal");
  doc.text(`Document généré le ${formatTimestamp(now)} - CONFIDENTIEL`, pageWidth / 2, pageHeight - 8, { align: "center" });
  
  // Add a watermark-style graphic element
  doc.setGState(new (doc as any).GState({ opacity: 0.03 }));
  doc.setFillColor(70, 130, 180);
  doc.circle(pageWidth - 50, 120, 60, 'F');
  
  // Reset opacity
  doc.setGState(new (doc as any).GState({ opacity: 1 }));
  
  // Save the PDF
  const filename = `Dossier_${patientData.last_name}_${patientData.first_name}_${patientData.file_number || ''}.pdf`;
  doc.save(filename);
  
  return doc;
};

// Generate inventory report PDF based on selected model type
export const generateInventoryReportPDF = async (reportData: any, modelType: 'standard' | 'detailed' | 'minimal' = 'standard') => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Set up different model formats based on type
  switch (modelType) {
    case 'detailed':
      return generateDetailedInventoryReportPDF(doc, reportData, pageWidth);
    case 'minimal':
      return generateMinimalInventoryReportPDF(doc, reportData, pageWidth);
    case 'standard':
    default:
      return generateStandardInventoryReportPDF(doc, reportData, pageWidth);
  }
};

// Standard inventory report format
const generateStandardInventoryReportPDF = (doc: jsPDF, reportData: any, pageWidth: number) => {
  // Add title
  doc.setFontSize(18);
  doc.setTextColor(44, 62, 80);
  doc.text("RAPPORT D'INVENTAIRE", pageWidth / 2, 15, { align: "center" });
  
  // Add period information
  doc.setFontSize(12);
  doc.text(`Période: ${reportData.periodName || "N/A"}`, 14, 25);
  doc.text(`Du: ${reportData.startDate ? new Date(reportData.startDate).toLocaleDateString() : "N/A"}`, 14, 32);
  doc.text(`Au: ${reportData.endDate ? new Date(reportData.endDate).toLocaleDateString() : "N/A"}`, 14, 39);
  
  // Add products table
  autoTable(doc, {
    head: [['Réf.', 'Nom', 'Dosage', 'Stock', 'Prix unitaire', 'Valeur']],
    body: reportData.products.map((product: any) => [
      product.code || '',
      product.name,
      product.dosage,
      product.currentStock || 0,
      product.unitPrice ? `${product.unitPrice.toFixed(2)} €` : '0.00 €',
      product.totalValue ? `${product.totalValue.toFixed(2)} €` : '0.00 €'
    ]),
    theme: "grid",
    startY: 45,
    headStyles: {
      fillColor: [41, 128, 185],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    alternateRowStyles: {
      fillColor: [240, 240, 240],
    },
    styles: {
      fontSize: 9,
      cellPadding: 3
    },
  });
  
  // Get the Y position after the table
  const tableResult = (doc as any).lastAutoTable as AutoTableResult;
  
  // Add summary
  const totalValue = reportData.products.reduce((sum: number, product: any) => sum + (product.totalValue || 0), 0);
  const lowStockItems = reportData.products.filter((p: any) => 
    p.minimumThreshold && p.currentStock <= p.minimumThreshold
  ).length;
  
  doc.setFontSize(12);
  doc.setTextColor(44, 62, 80);
  doc.text("RÉSUMÉ", 14, tableResult.finalY + 15);
  
  doc.setFontSize(10);
  doc.text(`Nombre total de produits: ${reportData.products.length}`, 14, tableResult.finalY + 25);
  doc.text(`Valeur totale de l'inventaire: ${totalValue.toFixed(2)} €`, 14, tableResult.finalY + 32);
  doc.text(`Produits en stock faible: ${lowStockItems}`, 14, tableResult.finalY + 39);
  
  // Add timestamp and footer
  const today = new Date();
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(
    `Document généré le ${today.toLocaleDateString()} à ${today.toLocaleTimeString()}`,
    pageWidth / 2,
    doc.internal.pageSize.getHeight() - 10,
    { align: "center" }
  );
  
  // Save the PDF
  doc.save(`Inventaire_${reportData.periodName}_${today.toISOString().split('T')[0]}.pdf`);
  
  return doc;
};

// Detailed inventory report format
const generateDetailedInventoryReportPDF = (doc: jsPDF, reportData: any, pageWidth: number) => {
  // Enhanced header with logo placeholder and title
  doc.setFillColor(220, 240, 255);
  doc.rect(0, 0, pageWidth, 30, "F");
  doc.setFontSize(20);
  doc.setTextColor(20, 60, 120);
  doc.text("RAPPORT D'INVENTAIRE DÉTAILLÉ", pageWidth / 2, 15, { align: "center" });
  doc.setFontSize(12);
  doc.text(`Période: ${reportData.periodName}`, pageWidth / 2, 25, { align: "center" });
  
  // Period information with more details
  doc.setFontSize(11);
  doc.setTextColor(50, 50, 50);
  
  // Create a box for period information
  doc.setFillColor(240, 247, 254);
  doc.roundedRect(14, 35, pageWidth - 28, 25, 3, 3, 'F');
  
  doc.text(`Date de début: ${reportData.startDate ? new Date(reportData.startDate).toLocaleDateString() : "N/A"}`, 20, 45);
  doc.text(`Date de fin: ${reportData.endDate ? new Date(reportData.endDate).toLocaleDateString() : "N/A"}`, 20, 53);
  
  // Products table with additional columns
  autoTable(doc, {
    head: [['Réf.', 'Nom du produit', 'Dosage', 'Stock actuel', 'Stock min.', 'Prix unitaire', 'Valeur totale', 'Statut']],
    body: reportData.products.map((product: any) => {
      // Determine stock status
      let status = "Normal";
      let statusColor = [0, 128, 0]; // Green
      
      if (product.minimumThreshold && product.currentStock <= product.minimumThreshold) {
        status = "Stock bas";
        statusColor = [220, 53, 69]; // Red
      } else if (product.minimumThreshold && product.currentStock <= product.minimumThreshold * 1.5) {
        status = "À surveiller";
        statusColor = [255, 193, 7]; // Yellow
      }
      
      return [
        product.code || '',
        product.name,
        product.dosage,
        product.currentStock || 0,
        product.minimumThreshold || 'N/A',
        product.unitPrice ? `${product.unitPrice.toFixed(2)} €` : '0.00 €',
        product.totalValue ? `${product.totalValue.toFixed(2)} €` : '0.00 €',
        status
      ];
    }),
    theme: "grid",
    startY: 65,
    headStyles: {
      fillColor: [41, 82, 150],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 10
    },
    alternateRowStyles: {
      fillColor: [240, 248, 255],
    },
    styles: {
      fontSize: 9,
      cellPadding: 4
    },
    columnStyles: {
      7: {
        halign: 'center',
        fontStyle: 'bold'
      }
    }
  });
  
  // Get the Y position after the table
  const tableResult = (doc as any).lastAutoTable as AutoTableResult;
  
  // Add detailed summary with charts or graphs if needed
  doc.setFillColor(220, 240, 255);
  doc.rect(14, tableResult.finalY + 10, pageWidth - 28, 30, "F");
  
  doc.setFontSize(14);
  doc.setTextColor(20, 60, 120);
  doc.text("RÉSUMÉ DÉTAILLÉ", pageWidth / 2, tableResult.finalY + 20, { align: "center" });
  
  // Calculate statistics
  const totalValue = reportData.products.reduce((sum: number, product: any) => sum + (product.totalValue || 0), 0);
  const lowStockItems = reportData.products.filter((p: any) => 
    p.minimumThreshold && p.currentStock <= p.minimumThreshold
  );
  const warningStockItems = reportData.products.filter((p: any) => 
    p.minimumThreshold && p.currentStock > p.minimumThreshold && p.currentStock <= p.minimumThreshold * 1.5
  );
  
  // Add summary statistics
  doc.setFontSize(10);
  doc.setTextColor(50, 50, 50);
  doc.text(`Nombre total de produits: ${reportData.products.length}`, 20, tableResult.finalY + 35);
  doc.text(`Valeur totale de l'inventaire: ${totalValue.toFixed(2)} €`, 20, tableResult.finalY + 43);
  doc.text(`Produits en stock faible: ${lowStockItems.length}`, 20, tableResult.finalY + 51);
  
  // List products that need attention
  if (lowStockItems.length > 0) {
    autoTable(doc, {
      head: [['PRODUITS EN STOCK FAIBLE']],
      body: lowStockItems.map((product: any) => [
        `${product.name} (${product.currentStock}/${product.minimumThreshold})`
      ]),
      theme: "grid",
      startY: tableResult.finalY + 65,
      headStyles: {
        fillColor: [220, 53, 69],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      styles: {
        fontSize: 8,
        cellPadding: 3
      }
    });
  }
  
  // Enhanced footer with more details
  const today = new Date();
  doc.setFillColor(240, 247, 254);
  doc.rect(0, doc.internal.pageSize.getHeight() - 20, pageWidth, 20, "F");
  doc.setFontSize(8);
  doc.setTextColor(50, 50, 50);
  doc.text(
    `Document généré le ${today.toLocaleDateString()} à ${today.toLocaleTimeString()} - CONFIDENTIEL`,
    pageWidth / 2,
    doc.internal.pageSize.getHeight() - 10,
    { align: "center" }
  );
  
  // Save the PDF
  doc.save(`Inventaire_Detaille_${reportData.periodName}_${today.toISOString().split('T')[0]}.pdf`);
  
  return doc;
};

// Minimal inventory report format
const generateMinimalInventoryReportPDF = (doc: jsPDF, reportData: any, pageWidth: number) => {
  // Simple header
  doc.setFontSize(16);
  doc.setTextColor(44, 62, 80);
  doc.text("SYNTHÈSE D'INVENTAIRE", pageWidth / 2, 15, { align: "center" });
  doc.setFontSize(10);
  doc.text(`Période: ${reportData.periodName} (${new Date(reportData.startDate).toLocaleDateString()} - ${new Date(reportData.endDate).toLocaleDateString()})`, 
    pageWidth / 2, 22, { align: "center" });
  
  // Simplified products table with just essential info
  autoTable(doc, {
    head: [['Produit', 'Stock', 'Valeur']],
    body: reportData.products.map((product: any) => [
      `${product.name} (${product.dosage})`,
      product.currentStock || 0,
      product.totalValue ? `${product.totalValue.toFixed(2)} €` : '0.00 €'
    ]),
    theme: "striped",
    startY: 30,
    headStyles: {
      fillColor: [100, 100, 100],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    styles: {
      fontSize: 9,
      cellPadding: 3
    }
  });
  
  // Get the Y position after the table
  const tableResult = (doc as any).lastAutoTable as AutoTableResult;
  
  // Simple summary
  const totalValue = reportData.products.reduce((sum: number, product: any) => sum + (product.totalValue || 0), 0);
  
  doc.setFontSize(10);
  doc.setTextColor(44, 62, 80);
  doc.text(`Total produits: ${reportData.products.length} | Valeur: ${totalValue.toFixed(2)} €`, 
    pageWidth / 2, tableResult.finalY + 15, { align: "center" });
  
  // Simple footer
  const today = new Date();
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(
    `Généré: ${today.toLocaleDateString()}`,
    pageWidth / 2,
    doc.internal.pageSize.getHeight() - 10,
    { align: "center" }
  );
  
  // Save the PDF
  doc.save(`Inventaire_Synthese_${reportData.periodName}.pdf`);
  
  return doc;
};

// Generate multiple formats
export const generateMultipleFormats = async (data: any, type: 'patient' | 'inventory', modelType?: string) => {
  console.log("Generating multiple formats for:", type, "with model type:", modelType);
  console.log("Data received:", data);
  
  if (type === 'patient' && 'last_name' in data && 'first_name' in data) {
    // Get appropriate model type for patient files
    const patientModelType = modelType as 'standard' | 'detailed' | 'compact' | 'professional';
    
    // Generate PDF
    const pdfDoc = await generatePatientFilePDF(data, patientModelType || 'professional');
    
    // Return different formats - for now just PDF, could add CSV/JSON exports
    return {
      pdf: pdfDoc,
      filename: `Dossier_${data.last_name}_${data.first_name}`,
    };
  } else if (type === 'inventory') {
    // Get appropriate model type for inventory reports
    const inventoryModelType = modelType as 'standard' | 'detailed' | 'minimal';
    
    // Generate PDF
    const pdfDoc = await generateInventoryReportPDF(data, inventoryModelType || 'standard');
    
    // Return different formats
    return {
      pdf: pdfDoc,
      filename: `Inventaire_${data.periodName}_${new Date().toISOString().split('T')[0]}`,
    };
  }
  
  console.error("Invalid data format for generation");
  return null;
};

